// Gestione utenti admin (master only) — Sprint 5.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IconButton } from '@audiq/ui';
import { KeyRound, Lock, ShieldOff, Trash2, Unlock } from 'lucide-react';
import { api, ApiClientError, type AdminUserRow } from '../../lib/api';
import { PrimaryButton, SettingsCard, inputStyle } from './_shared';

export function UsersPage() {
  const qc = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me() });
  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.listAdminUsers(),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [createdInfo, setCreatedInfo] = useState<{
    email: string;
    role: string;
    temp_password: string | null;
    sent: boolean;
  } | null>(null);

  return (
    <div className="space-y-6">
      <SettingsCard
        kicker="Utenti admin"
        title="Amministratori"
        description="Crea, lock, reset password/2FA per gli admin di questa installazione."
        rightSlot={<PrimaryButton onClick={() => setShowCreate(true)}>+ Nuovo admin</PrimaryButton>}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: 'var(--mute)' }} className="mono text-xs">
                <th className="ps-1 pe-3 py-2 text-start">Email</th>
                <th className="px-3 py-2 text-start">Ruolo</th>
                <th className="px-3 py-2 text-start">2FA</th>
                <th className="px-3 py-2 text-start">Ultimo accesso</th>
                <th className="px-3 py-2 text-start">Stato</th>
                <th className="px-3 py-2 text-end">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.data?.admins.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === meQuery.data?.user.id}
                  onChange={() => qc.invalidateQueries({ queryKey: ['admin', 'users'] })}
                />
              ))}
            </tbody>
          </table>
        </div>
      </SettingsCard>

      {showCreate && (
        <CreateAdminModal
          onClose={() => setShowCreate(false)}
          onCreated={(info) => {
            setShowCreate(false);
            setCreatedInfo(info);
            qc.invalidateQueries({ queryKey: ['admin', 'users'] });
          }}
        />
      )}

      {createdInfo && (
        <Modal title="Admin creato" onClose={() => setCreatedInfo(null)}>
          <p className="mb-2 text-sm" style={{ color: 'var(--ink)' }}>
            Account creato: <strong>{createdInfo.email}</strong> ({createdInfo.role})
          </p>
          {createdInfo.sent ? (
            <p className="text-sm" style={{ color: 'var(--mute)' }}>
              Email di invito inviata con la password temporanea.
            </p>
          ) : (
            <>
              <p className="mb-2 text-sm" style={{ color: 'var(--accent)' }}>
                Password temporanea (visibile solo una volta — copiala ora):
              </p>
              <div
                className="mono mb-3 break-all rounded-md border p-3 text-sm"
                style={{ borderColor: 'var(--line)', background: 'var(--soft)' }}
              >
                {createdInfo.temp_password}
              </div>
              <button
                onClick={() => navigator.clipboard.writeText(createdInfo.temp_password ?? '')}
                className="rounded-md border px-3 py-1.5 text-xs"
                style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
              >
                Copia password
              </button>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}

function UserRow({
  user,
  isSelf,
  onChange,
}: {
  user: AdminUserRow;
  isSelf: boolean;
  onChange: () => void;
}) {
  const isLocked = user.locked_until && new Date(user.locked_until) > new Date();
  const [actionResult, setActionResult] = useState<string | null>(null);

  const resetPwd = useMutation({
    mutationFn: () => api.resetAdminPassword(user.id, true),
    onSuccess: (data) => {
      onChange();
      if (data.email_sent) setActionResult('✓ Email inviata');
      else if (data.temporary_password)
        setActionResult(`Password: ${data.temporary_password.slice(0, 6)}…`);
    },
  });
  const reset2fa = useMutation({
    mutationFn: () => api.reset2faAdmin(user.id),
    onSuccess: () => {
      onChange();
      setActionResult('✓ 2FA reset');
    },
  });
  const lockAction = useMutation({
    mutationFn: () => api.lockAdmin(user.id),
    onSuccess: () => {
      onChange();
      setActionResult('✓ Lockato');
    },
  });
  const unlockAction = useMutation({
    mutationFn: () => api.unlockAdmin(user.id),
    onSuccess: () => {
      onChange();
      setActionResult('✓ Sbloccato');
    },
  });
  const deleteAction = useMutation({
    mutationFn: () => api.deleteAdmin(user.id),
    onSuccess: () => {
      onChange();
      setActionResult('✓ Eliminato');
    },
  });

  return (
    <tr style={{ borderTop: '1px solid var(--line)' }}>
      <td className="ps-1 pe-3 py-2" style={{ color: 'var(--ink)' }}>
        {user.email}{' '}
        {isSelf && (
          <span className="mono text-xs" style={{ color: 'var(--mute)' }}>
            (tu)
          </span>
        )}
      </td>
      <td className="px-3 py-2" style={{ color: 'var(--ink)' }}>
        {user.role}
      </td>
      <td className="px-3 py-2" style={{ color: 'var(--ink)' }}>
        {user.totp_enabled
          ? '✓'
          : user.must_setup_totp
            ? user.grace_until && new Date(user.grace_until) > new Date()
              ? '⚠ in grace'
              : '✗ obbligatorio'
            : '—'}
      </td>
      <td className="px-3 py-2 mono text-xs" style={{ color: 'var(--mute)' }}>
        {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('it-IT') : 'mai'}
      </td>
      <td className="px-3 py-2 mono text-xs">
        {isLocked ? (
          <span style={{ color: 'var(--danger)' }}>Locked</span>
        ) : (
          <span style={{ color: 'var(--primary)' }}>OK</span>
        )}
      </td>
      <td className="px-3 py-2 text-end">
        <div className="flex items-center justify-end gap-1">
          {actionResult && (
            <span className="mono me-2 text-xs" style={{ color: 'var(--mute)' }}>
              {actionResult}
            </span>
          )}
          <IconButton
            icon={KeyRound}
            label="Reset password"
            onClick={() => resetPwd.mutate()}
            disabled={resetPwd.isPending}
          />
          <IconButton
            icon={ShieldOff}
            label="Reset 2FA"
            onClick={() => reset2fa.mutate()}
            disabled={reset2fa.isPending || (!user.totp_enabled && !user.must_setup_totp)}
          />
          {isLocked ? (
            <IconButton
              icon={Unlock}
              label="Sblocca"
              onClick={() => unlockAction.mutate()}
              disabled={unlockAction.isPending}
            />
          ) : (
            <IconButton
              icon={Lock}
              label="Lock"
              tone="danger"
              onClick={() => lockAction.mutate()}
              disabled={lockAction.isPending || isSelf}
            />
          )}
          <IconButton
            icon={Trash2}
            label="Elimina"
            tone="danger"
            onClick={() => {
              if (confirm(`Eliminare ${user.email}?`)) deleteAction.mutate();
            }}
            disabled={deleteAction.isPending || isSelf}
          />
        </div>
      </td>
    </tr>
  );
}

function CreateAdminModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (info: {
    email: string;
    role: string;
    temp_password: string | null;
    sent: boolean;
  }) => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'master' | 'operator' | 'viewer'>('operator');
  const [sendInvite, setSendInvite] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.createAdminUser({ email, role, send_invite_email: sendInvite }),
    onSuccess: (data) => {
      onCreated({
        email: data.admin.email,
        role: data.admin.role,
        temp_password: data.temporary_password,
        sent: data.invite_email_sent,
      });
    },
    onError: (err) => setError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  return (
    <Modal title="Nuovo admin" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
        className="space-y-3"
      >
        <input
          type="email"
          required
          placeholder="email@dominio.it"
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={inputStyle()}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <select
          className="w-full rounded-md border px-3 py-2 text-sm"
          style={inputStyle()}
          value={role}
          onChange={(e) => setRole(e.target.value as typeof role)}
        >
          <option value="viewer">Viewer (solo lettura)</option>
          <option value="operator">Operator (lettura + note)</option>
          <option value="master">Master (full + 2FA obbligatorio)</option>
        </select>
        <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--ink)' }}>
          <input
            type="checkbox"
            checked={sendInvite}
            onChange={(e) => setSendInvite(e.target.checked)}
          />
          Invia email di invito (richiede SMTP configurato)
        </label>
        {error && (
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            {error}
          </p>
        )}
        <div className="flex gap-2">
          <PrimaryButton type="submit" disabled={mutation.isPending || !email}>
            {mutation.isPending ? 'Creo…' : 'Crea'}
          </PrimaryButton>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-4 py-2 text-sm"
            style={{ borderColor: 'var(--line)', color: 'var(--ink)' }}
          >
            Annulla
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border p-6 shadow-lg"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="serif mb-4 text-xl" style={{ color: 'var(--ink)' }}>
          {title}
        </h3>
        {children}
      </div>
    </div>
  );
}
